import { DictionaryHelper } from '@/helpers/dictionary.helper';
import { Injectable } from '@angular/core';
import { Attribute, AttributeGroup, DataDictionaryService, PropertyCategory, PropertyType } from '@eemo/api-client';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class DictionaryCacheService {
  private versionSubject: BehaviorSubject<string>;
  public version: Observable<string>;
  public versionValue: string;
  groups: { [key: string]: AttributeGroup };
  groupsArray: AttributeGroup[];
  attributes: { [key: string]: Attribute };
  attributesArray: Attribute[];

  public static sortAttrGroup(a: AttributeGroup, b: AttributeGroup): number {
    const orderA = a.display.order ? a.display.order : 100;
    const orderB = b.display.order ? b.display.order : 100;
    if (orderA > orderB) {
      return 1;
    }
    if (orderA < orderB) {
      return -1;
    }
    return 0;
  }

  public static sortAttr(a: Attribute, b: Attribute): number {
    const orderA = a.display.order ? a.display.order : 100;
    const orderB = b.display.order ? b.display.order : 100;
    if (orderA > orderB) {
      return 1;
    }
    if (orderA < orderB) {
      return -1;
    }
    return 0;
  }

  public static sortSummaryAttr(a: Attribute, b: Attribute): number {
    const categories = Object.keys(PropertyCategory);
    const orderA = a.display.order ? a.display.order : 100;
    const orderB = b.display.order ? b.display.order : 100;
    const categoryOrderA = categories.indexOf(a.category);
    const categoryOrderB = categories.indexOf(b.category);

    if (categoryOrderA > categoryOrderB || orderA > orderB) {
      return 1;
    }
    if (categoryOrderA < categoryOrderB || orderA < orderB) {
      return -1;
    }
    return 0;
  }

  constructor(private dictionaryService: DataDictionaryService, private dictHelper: DictionaryHelper) {
    this.versionSubject = new BehaviorSubject<string>(localStorage.getItem('DICT_VERSION'));
    this.versionValue = this.versionSubject.value;
    this.version = this.versionSubject.asObservable();
    this.version.subscribe((version) => {
      this.versionValue = version;
    });
    this.groups = JSON.parse(localStorage.getItem('DICT_GROUPS'));
    this.groupsArray = JSON.parse(localStorage.getItem('DICT_GROUPS_ARRAY'));
    this.attributes = JSON.parse(localStorage.getItem('DICT_ATTRIBUTES'));
    this.attributesArray = JSON.parse(localStorage.getItem('DICT_ATTRIBUTES_ARRAY'));
  }

  checkDictionaryVersion(force?: boolean) {
    const lastCheck = parseInt(localStorage.getItem('DICT_VERSION_LASTCHECK'), 10);
    const nextCheck = lastCheck + 7200000;
    if (force || !lastCheck || nextCheck < new Date().getTime()) {
      // check the version
      this.dictionaryService.getDictionaryVersion().subscribe((version) => {
        if (this.versionValue !== version) {
          this.refreshDictionaryCache(version);
        }
      });
    }
  }

  refreshDictionaryCache(version: string) {
    forkJoin([
      this.dictionaryService.getDictionaryGroups(),
      this.dictionaryService.getDictionaryAttributes()
    ]).subscribe(([groupData, attrData]) => {
      // groups
      this.groupsArray = groupData;
      localStorage.setItem('DICT_GROUPS_ARRAY', JSON.stringify(groupData));
      const groups = {};
      groupData.forEach((group) => {
        groups[group.code] = group;
      });
      this.groups = groups;
      localStorage.setItem('DICT_GROUPS', JSON.stringify(groups));

      // attributes
      this.attributesArray = attrData;
      localStorage.setItem('DICT_ATTRIBUTES_ARRAY', JSON.stringify(attrData));
      const attrs = {};
      attrData.forEach((attr) => {
        attrs[attr.code] = attr;
      });
      this.attributes = attrs;
      localStorage.setItem('DICT_ATTRIBUTES', JSON.stringify(attrs));

      localStorage.setItem('DICT_VERSION', version);
      this.versionSubject.next(version);
      localStorage.setItem('DICT_VERSION_LASTCHECK', new Date().getTime().toString());
    });
  }

  getAttributeGroupsForCategory(category: PropertyCategory): AttributeGroup[] {
    return this.groupsArray.filter((grp) => grp.category === category).sort(DictionaryCacheService.sortAttrGroup);
  }

  getAttrDisplayNameFromCode(code: string) {
    return this.dictHelper.getAttrNameDisplay(this.attributes[code]);
  }

  getAttrDisplayValueFromCode(code: string, value: string) {
    return this.dictHelper.getAttrValueDisplay(this.attributes[code], value);
  }

  getAttributesForGroup(groupCode: string): Attribute[] {
    return this.attributesArray.filter((attr) => attr.groupCode === groupCode).sort(DictionaryCacheService.sortAttr);
  }

  getAttributesForGroupAndPropertyTypes(groupCode: string, propertyTypes: PropertyType[]): Attribute[] {
    return this.attributesArray
      .filter(
        (attr) =>
          attr.groupCode === groupCode &&
          (!attr.allowedPropertyTypes ||
            attr.allowedPropertyTypes.filter((value) => propertyTypes.includes(value)).length > 0)
      )
      .sort(DictionaryCacheService.sortAttr);
  }

  getAttributesForCategory(category: PropertyCategory): Attribute[] {
    return this.attributesArray.filter((attr) => attr.category === category).sort(DictionaryCacheService.sortAttr);
  }

  getAttributesForSummary(): Attribute[] {
    return this.attributesArray
      .filter((attr) => attr.summary)
      .sort((a, b) => {
        const categories = Object.keys(PropertyCategory);
        const orderA = a.display.order ? a.display.order : 100;
        const orderB = b.display.order ? b.display.order : 100;
        const categoryOrderA = categories.indexOf(a.category);
        const categoryOrderB = categories.indexOf(b.category);

        const groupOrderA = this.groups[a.groupCode].display.order;
        const groupOrderB = this.groups[b.groupCode].display.order;

        if (
          categoryOrderA > categoryOrderB ||
          (categoryOrderA === categoryOrderB && groupOrderA > groupOrderB) ||
          (categoryOrderA === categoryOrderB && groupOrderA === groupOrderB && orderA > orderB)
        ) {
          return 1;
        }
        if (
          categoryOrderA < categoryOrderB ||
          (categoryOrderA === categoryOrderB && groupOrderA < groupOrderB) ||
          (categoryOrderA === categoryOrderB && groupOrderA === groupOrderB && orderA < orderB)
        ) {
          return -1;
        }
        return 0;
      });
  }

  getAttributesForRescue(): Attribute[] {
    return this.attributesArray
      .filter((attr) => attr.rescue)
      .sort((a, b) => {
        const categories = Object.keys(PropertyCategory);
        const orderA = a.display.order ? a.display.order : 100;
        const orderB = b.display.order ? b.display.order : 100;
        const categoryOrderA = categories.indexOf(a.category);
        const categoryOrderB = categories.indexOf(b.category);

        const groupOrderA = this.groups[a.groupCode].display.order;
        const groupOrderB = this.groups[b.groupCode].display.order;

        if (
          categoryOrderA > categoryOrderB ||
          (categoryOrderA === categoryOrderB && groupOrderA > groupOrderB) ||
          (categoryOrderA === categoryOrderB && groupOrderA === groupOrderB && orderA > orderB)
        ) {
          return 1;
        }
        if (
          categoryOrderA < categoryOrderB ||
          (categoryOrderA === categoryOrderB && groupOrderA < groupOrderB) ||
          (categoryOrderA === categoryOrderB && groupOrderA === groupOrderB && orderA < orderB)
        ) {
          return -1;
        }
        return 0;
      });
  }

  getAttributesRequiredForCertification(propType: PropertyType): Attribute[] {
    return this.attributesArray.filter(
      (attr) =>
        attr.required &&
        attr.inCertification &&
        (!attr.allowedPropertyTypes ||
          attr.allowedPropertyTypes.length === 0 ||
          attr.allowedPropertyTypes.indexOf(propType) > -1)
    );
  }
}
